import {
  IconCopy,
  IconHome2,
  IconRefresh,
  TablerIcon,
} from "@tabler/icons-react";
import { ReactNode } from "react";
import { __ } from "@common/helpers/i18n";
import { cn } from "@app/lib/utils";
import {
  Card,
  CardContent,
  FooterActions,
  statePageSectionClassName,
} from "../container";
import { Button } from "../form";
import { BackIcon } from "../helpers";

type ErrorPageShellProps = {
  code: string;
  description: string;
  icon: TablerIcon;
  primaryActionIcon?: ReactNode;
  primaryActionLabel?: ReactNode;
  primaryActionTo?: string;
  primaryAction?: "dashboard" | "refresh";
  showBackAction?: boolean;
  showCode?: boolean;
  standalone?: boolean;
  tone?: "danger" | "warning" | "primary" | "muted";
  title: string;
  traceId?: string;
  children?: ReactNode;
};

const toneClasses = {
  danger: {
    icon: "bg-destructive/10 text-destructive ring-destructive/15",
    code: "text-destructive",
  },
  warning: {
    icon: "bg-warning/10 text-warning ring-warning/15",
    code: "text-warning",
  },
  primary: {
    icon: "bg-primary/10 text-primary ring-primary/15",
    code: "text-primary",
  },
  muted: {
    icon: "bg-muted text-muted-foreground ring-border",
    code: "text-muted-foreground",
  },
} as const;

export default function ErrorPageShell({
  code,
  description,
  icon: Icon,
  primaryActionIcon,
  primaryActionLabel,
  primaryActionTo,
  primaryAction = "refresh",
  showBackAction = false,
  showCode = true,
  standalone = true,
  tone = "danger",
  title,
  traceId,
  children,
}: ErrorPageShellProps) {
  const reloadPage = () => window.location.reload();
  const goToDashboard = () => {
    const dashboardUrl = new URL("/dashboard", window.location.origin);
    dashboardUrl.searchParams.set(
      "recovered_from_error",
      Date.now().toString(),
    );

    window.location.replace(dashboardUrl.toString());
  };
  const backPage = () => {
    if (window.history.length > 1) {
      window.history.back();
      return;
    }

    window.location.assign("/dashboard");
  };
  const copyTraceId = () => {
    if (traceId) {
      void navigator.clipboard?.writeText(traceId);
    }
  };

  const primaryButton =
    primaryAction === "dashboard" ? (
      <Button
        variant="primary"
        onClick={goToDashboard}
        icon={primaryActionIcon || <IconHome2 size={18} stroke={2} />}
        className="w-full max-w-64 justify-center sm:w-auto"
      >
        {primaryActionLabel || __("Back to dashboard")}
      </Button>
    ) : primaryActionTo ? (
      <Button
        variant="primary"
        to={primaryActionTo}
        icon={primaryActionIcon || <IconHome2 size={18} stroke={2} />}
        className="w-full max-w-64 justify-center sm:w-auto"
      >
        {primaryActionLabel || __("Back to dashboard")}
      </Button>
    ) : (
      <Button
        variant="primary"
        onClick={reloadPage}
        icon={primaryActionIcon || <IconRefresh size={18} stroke={2} />}
        className="w-full max-w-64 justify-center sm:w-auto"
      >
        {primaryActionLabel || __("Refresh page")}
      </Button>
    );

  const content = (
    <Card
      className={cn(
        "w-full overflow-hidden",
        standalone && "max-w-2xl shadow-soft",
      )}
    >
      <CardContent className="flex min-h-[360px] items-center justify-center px-5 py-10 text-center sm:px-6 sm:py-12 md:min-h-[380px] md:px-8 md:py-14">
        <div className="mx-auto flex max-w-md flex-col items-center">
          <span
            className={cn(
              "flex h-14 w-14 shrink-0 items-center justify-center rounded-md ring-1 [&_svg]:h-7 [&_svg]:w-7 [&_svg]:stroke-[2]",
              toneClasses[tone].icon,
            )}
          >
            <Icon />
          </span>

          {showCode && (
            <div
              className={cn(
                "mt-5 text-4xl font-black leading-none tracking-normal sm:text-5xl",
                toneClasses[tone].code,
              )}
              dir="ltr"
            >
              {code}
            </div>
          )}

          <h1
            className={cn(
              "text-xl font-extrabold leading-8 text-foreground sm:text-2xl",
              showCode ? "mt-3" : "mt-5",
            )}
          >
            {title}
          </h1>
          <p className="mt-2 text-sm font-medium leading-7 text-muted-foreground">
            {description}
          </p>

          {children && <div className="mt-5 w-full">{children}</div>}

          <div className="mt-6 w-full">
            {traceId && (
              <div className="mb-4 flex flex-col items-center text-center">
                <p className="text-xs font-semibold leading-5 text-muted-foreground">
                  {__("Trace ID")}
                </p>
                <button
                  type="button"
                  onClick={copyTraceId}
                  className="mt-1 inline-flex max-w-full min-w-0 items-center justify-center gap-2 rounded-md px-2 py-1 text-xs font-semibold leading-5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/15"
                >
                  <span className="min-w-0 truncate text-center" dir="ltr">
                    {traceId}
                  </span>
                  <IconCopy
                    size={15}
                    stroke={2}
                    className="shrink-0 text-muted-foreground"
                  />
                </button>
              </div>
            )}

            <FooterActions
              align="center"
              className="mx-auto items-center justify-center"
            >
              {showBackAction && (
                <Button
                  type="button"
                  variant="gray"
                  onClick={backPage}
                  icon={<BackIcon size={18} stroke={2} />}
                  className="w-full max-w-64 justify-center sm:w-auto"
                >
                  {__("Back")}
                </Button>
              )}
              {primaryButton}
              {primaryAction === "refresh" && (
                <Button
                  variant="gray"
                  onClick={goToDashboard}
                  icon={<IconHome2 size={18} stroke={2} />}
                  className="w-full max-w-64 justify-center sm:w-auto"
                >
                  {__("Back to dashboard")}
                </Button>
              )}
            </FooterActions>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (!standalone) {
    return (
      <div className={`${statePageSectionClassName} text-foreground`}>
        {content}
      </div>
    );
  }

  return (
    <div
      className="flex min-h-full flex-1 items-center justify-center bg-background px-4 py-6 text-foreground sm:px-6"
    >
      {content}
    </div>
  );
}
